import { Component, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';

@Component({
    selector: 'auto-advanced-general-settings',
    templateUrl: './advanced-general-settings.component.html',
    styleUrls: ['./advanced-general-settings.component.scss'],
    standalone: false
})
export class AdvancedGeneralSettingsComponent implements OnInit, OnChanges {
  @Input() data: any | null = null;
  @Output() dataEmitter = new EventEmitter<UntypedFormGroup>();
  @Output() preCacheRequested = new EventEmitter<void>();
  form: UntypedFormGroup;

  preCacheBusy = false;
  @Input() set preCacheBusyInput(value: boolean) {
    this.preCacheBusy = !!value;
  }

  oemProgramOptions = [
    { label: 'None', value: 'NONE' },
    { label: 'Acura Program', value: 'ACURA' },
    { label: 'Audi Program', value: 'AUDI' },
    { label: 'Ford Direct Program', value: 'FORD_DIRECT' },
    { label: 'Honda Program', value: 'HONDA' },
    { label: 'Stellantis Program', value: 'STELLANTIS_RTM' },
  ];

  constructor(private formBuilder: UntypedFormBuilder) {}

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      preCacheSpecials: [false],
      emailVersion: [1],
      enhancedSeoEnabled: [false],
      oemProgramsSummary: [''],
      showDashboardReport: [false],
      adobeTaggingEnabled: [false],
      sharedOfferSetsEnabled: [false],
      dynamicCreativeEnabled: [false],
      mediaAdCopyBulkUpdateEnabled: [false],
      oemProgramSelect: ['NONE'],
      inAcuraProgram: [false],
      inAudiProgram: [false],
      inHondaProgram: [false],
      inFordDirectProgram: [false],
      fordDirectDealerId: [''],
      stellantisReportingTag: [false],
      fcaLastContacted: [''],
      fcaBusinessCenter: [''],
      hasTrimFeaturedSpecials: [false]
    });

    this.dataEmitter.emit(this.form);
    this.patchFromInput(this.data);

    this.form.get('oemProgramSelect')?.valueChanges.subscribe((val) => this.applyOemSelection(val));

  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      this.patchFromInput(changes['data'].currentValue);
    }
  }

  private applyOemSelection(selection: string): void {
    this.form.patchValue({
      inAcuraProgram: selection === 'ACURA',
      inAudiProgram: selection === 'AUDI',
      inHondaProgram: selection === 'HONDA',
      inFordDirectProgram: selection === 'FORD_DIRECT',
      stellantisReportingTag: selection === 'STELLANTIS_RTM',
    }, { emitEvent: false });
  }

  private deriveDropdownFromBooleans(general: any): string {
    const programMapping: { [key: string]: string } = {
      'stellantisReportingTag': 'STELLANTIS_RTM',
      'inHondaProgram': 'HONDA',
      'inAudiProgram': 'AUDI',
      'inAcuraProgram': 'ACURA',
      'inFordDirectProgram': 'FORD_DIRECT'
    };

    let result = 'NONE';

    for (const [property, value] of Object.entries(programMapping)) {
      if (general?.[property] && result === 'NONE') {
        result = value;
      }
    }

    return result;
  }

  private patchFromInput(generalAdvancedSettings: any): void {
    const hasFormAndInput = !!generalAdvancedSettings && !!this.form;
    if (hasFormAndInput) {
      const oemSel = this.deriveDropdownFromBooleans(generalAdvancedSettings);

      this.form.patchValue({
        preCacheSpecials: !!generalAdvancedSettings.preCacheSpecials,
        emailVersion: Number(generalAdvancedSettings.emailToolVersion ?? 1),
        enhancedSeoEnabled: !!generalAdvancedSettings.enhancedSeoEnabled,
        showDashboardReport: !!generalAdvancedSettings.showDashboardReport,
        oemProgramSelect: oemSel,
        adobeTaggingEnabled: !!generalAdvancedSettings.adobeTaggingEnabled,
        inAcuraProgram: !!(generalAdvancedSettings.inAcuraProgram ?? this.form.controls.inAcuraProgram.value ?? false),
        inAudiProgram: !!(generalAdvancedSettings.inAudiProgram ?? this.form.controls.inAudiProgram.value ?? false),
        inHondaProgram: !!(generalAdvancedSettings.inHondaProgram ?? this.form.controls.inHondaProgram.value ?? false),
        inFordDirectProgram: !!(generalAdvancedSettings.inFordDirectProgram ?? this.form.controls.inFordDirectProgram.value ?? false),
        fordDirectDealerId: generalAdvancedSettings.fordDirectDealerId ?? this.form.controls.fordDirectDealerId.value ?? '',
        stellantisReportingTag: !!(generalAdvancedSettings.stellantisReportingTag ?? this.form.controls.stellantisReportingTag.value ?? false),
        dynamicCreativeEnabled: !!generalAdvancedSettings.dynamicCreativeEnabled,
        mediaAdCopyBulkUpdateEnabled: !!generalAdvancedSettings.mediaAdCopyBulkUpdateEnabled,
      }, {emitEvent: false});

      this.form.markAsPristine();
    }
  }

  get preCacheLabel(): string {
    let label = 'Refresh Ad Cache';

    if (this.preCacheBusy) {
      label = 'Refreshing Ad Cache...';
    }

    return label;
  }

  get preCacheIcon(): string {
    let icon = 'pi pi-refresh';

    if (this.preCacheBusy) {
      icon = 'pi pi-refresh pi-spin';
    }

    return icon;
  }

  onPreCacheClick(): void {
    if (!this.preCacheBusy) {
      this.preCacheRequested.emit();
    }
  }
}
